// Mise en forme des dates et nombres, au format du Système (« JEU 08.10 », « J-2 », « 99,4 kg »).
// Logique pure, testée sans navigateur.

export type Jour = string; // « AAAA-MM-JJ »

const JOURS = ['DIM', 'LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM'];
const JOURS_LONGS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

function utc(j: Jour): Date {
  const [a, m, d] = j.split('-').map(Number);
  return new Date(Date.UTC(a ?? 1970, (m ?? 1) - 1, d ?? 1));
}

export function ecartJours(de: Jour, a: Jour): number {
  return Math.round((utc(a).getTime() - utc(de).getTime()) / 86_400_000);
}

export function ajouterJours(j: Jour, n: number): Jour {
  return new Date(utc(j).getTime() + n * 86_400_000).toISOString().slice(0, 10);
}

/** « JEU 08 » */
export function jourCourt(j: Jour): string {
  return `${JOURS[utc(j).getUTCDay()]} ${j.slice(8, 10)}`;
}

/** « JEU 08.10 » */
export function jourMois(j: Jour): string {
  return `${jourCourt(j)}.${j.slice(5, 7)}`;
}

/** « 08.10 » */
export function jjmm(j: Jour): string {
  return `${j.slice(8, 10)}.${j.slice(5, 7)}`;
}

export function jourLong(j: Jour): string {
  return JOURS_LONGS[utc(j).getUTCDay()] ?? '';
}

/** « AUJ. », « DEMAIN », « J-3 », ou « J+2 » pour le passé. */
export function compteARebours(today: Jour, j: Jour): string {
  const n = ecartJours(today, j);
  if (n === 0) return 'AUJ.';
  if (n === 1) return 'DEMAIN';
  return n > 0 ? `J-${n}` : `J+${-n}`;
}

export function kg(n: number | null): string {
  return n === null ? '—' : `${n.toFixed(1).replace('.', ',')} kg`;
}

/**
 * La tendance d'une mesure : flèche, écart signé, et si c'est un progrès. Pour la masse grasse,
 * baisser est un progrès ; pour la masse musculaire, monter.
 */
export function tendance(ecart: number | null, progresSiBaisse: boolean): { texte: string; sens: 'mieux' | 'moins' | 'stable' } | null {
  if (ecart === null) return null;
  if (ecart === 0) return { texte: '= stable', sens: 'stable' };
  const texte = `${ecart > 0 ? '▲ +' : '▼ −'}${Math.abs(ecart).toFixed(1).replace('.', ',')}`;
  return { texte, sens: (ecart < 0) === progresSiBaisse ? 'mieux' : 'moins' };
}

/** Saisie française tolérée : « 99,4 », « 99.4 », « 99,4 kg ». */
export function lirePoids(saisie: string): number | null {
  const n = Number(saisie.replace(',', '.').replace(/[^\d.]/g, ''));
  return Number.isFinite(n) && n >= 30 && n <= 300 ? Math.round(n * 10) / 10 : null;
}

/** Part du chemin parcouru vers l'objectif, entre 0 et 1. */
export function progressionPoids(depart: number | null, actuel: number | null, objectif: number | null): number {
  if (depart === null || actuel === null || objectif === null || depart === objectif) return 0;
  return Math.min(1, Math.max(0, (depart - actuel) / (depart - objectif)));
}

/** Date locale du navigateur, « AAAA-MM-JJ ». */
export function aujourdhui(d = new Date()): Jour {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
